import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileText, ClipboardCheck, Package } from "lucide-react";
import { istToday } from "@/lib/dateFormat";

export default function DataQualityPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("Admin") || (user?.roles || []).includes("admin") || (user?.roles || []).includes("Finance Manager");
  const [loading, setLoading] = useState({});
  const today = istToday();
  const monthStart = today.slice(0, 8) + "01";
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [orderSearch, setOrderSearch] = useState("");

  if (!isAdmin) return <div className="p-8 text-center"><p className="text-zinc-400">Admin or Finance Manager access required.</p></div>;

  const download = async (key, url, filename) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      const r = await api.get(url, { responseType: "blob" });
      const u = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
      window.URL.revokeObjectURL(u);
      toast.success(`${filename} downloaded`);
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 400 ? "No data found for the given filters" : "Download failed");
    } finally { setLoading(prev => ({ ...prev, [key]: false })); }
  };

  return (
    <div data-testid="data-quality-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mb-2">Data Quality & Audit Reports</h1>
      <p className="text-sm text-zinc-500 mb-6">Download diagnostic reports for data completeness, audit trails, and order master records.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Report 1: SKU Data Quality */}
        <div className="border border-zinc-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2"><Package size={18} className="text-blue-600" /><h3 className="text-sm font-semibold">SKU Data Quality</h3></div>
          <p className="text-[10px] text-zinc-500 mb-3">Identifies SKUs with missing COGS, MRP, or Sales Channel pricing. 4 sheets: Missing COGS, Missing MRP, No Channel Pricing, Summary.</p>
          <Button onClick={() => download("sku", "/admin/data-quality/skus", `SKU_Data_Quality_${today}.xlsx`)}
            disabled={loading.sku} className="w-full rounded-sm h-8 text-xs" data-testid="download-sku-quality">
            <Download size={14} /> {loading.sku ? "Generating..." : "Download SKU Quality Report"}
          </Button>
        </div>

        {/* Report 2: Order Data Quality */}
        <div className="border border-zinc-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2"><ClipboardCheck size={18} className="text-amber-600" /><h3 className="text-sm font-semibold">Order Data Quality</h3></div>
          <p className="text-[10px] text-zinc-500 mb-2">Identifies orders with missing fields — PO#, invoice date, warehouse, logistics info, etc. Flags severity per field.</p>
          <div className="flex gap-2 mb-2">
            <div className="flex-1"><Label className="text-[9px]">From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-xs rounded-sm" /></div>
            <div className="flex-1"><Label className="text-[9px]">To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-xs rounded-sm" /></div>
          </div>
          <Button onClick={() => download("orders", `/admin/data-quality/orders?date_from=${dateFrom}&date_to=${dateTo}`, `Order_Data_Quality_${today}.xlsx`)}
            disabled={loading.orders} className="w-full rounded-sm h-8 text-xs" data-testid="download-order-quality">
            <Download size={14} /> {loading.orders ? "Generating..." : "Download Order Quality Report"}
          </Button>
        </div>

        {/* Report 3: Super Master */}
        <div className="border border-zinc-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2"><FileText size={18} className="text-green-600" /><h3 className="text-sm font-semibold">Super Master Report</h3></div>
          <p className="text-[10px] text-zinc-500 mb-2">Complete order details — 7 sheets: Order Master, SKU Lines, Payments, Deductions, Status History, Comments, Logistics. Every field captured.</p>
          <div className="flex gap-2 mb-2">
            <div className="flex-1"><Label className="text-[9px]">From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-xs rounded-sm" /></div>
            <div className="flex-1"><Label className="text-[9px]">To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-xs rounded-sm" /></div>
          </div>
          <div className="mb-2"><Label className="text-[9px]">Or search by Order #</Label><Input value={orderSearch} onChange={e => setOrderSearch(e.target.value)} placeholder="e.g. ORD-00123" className="h-7 text-xs rounded-sm" /></div>
          <Button onClick={() => {
            const params = orderSearch ? `order_number=${encodeURIComponent(orderSearch)}` : `date_from=${dateFrom}&date_to=${dateTo}`;
            download("master", `/admin/super-master-report?${params}`, `Super_Master_Report_${today}.xlsx`);
          }} disabled={loading.master} className="w-full rounded-sm h-8 text-xs bg-green-600 hover:bg-green-700" data-testid="download-super-master">
            <Download size={14} /> {loading.master ? "Generating..." : "Download Super Master Report"}
          </Button>
        </div>
      </div>

      <div className="mt-6 border border-blue-200 bg-blue-50 p-3 rounded-sm text-xs text-blue-800">
        <p className="font-semibold mb-1">Super Master Report includes 7 sheets:</p>
        <ol className="list-decimal ml-4 space-y-0.5 text-[10px]">
          <li><strong>Order Master</strong> — All header fields: dates, customer, channel, GST breakup, invoice, payment status, freight</li>
          <li><strong>SKU Lines</strong> — Every line item: SKU code, name, qty, MRP, selling price, discount %, GST, line totals</li>
          <li><strong>Payments</strong> — All payment entries: date, amount, mode, UTR/reference, notes, recorded by</li>
          <li><strong>Deductions</strong> — All deductions: type (TDS/CN/etc), amount, reference, notes</li>
          <li><strong>Status History</strong> — Full audit trail: every status change with timestamp and user</li>
          <li><strong>Comments</strong> — All internal notes and comments on each order</li>
          <li><strong>Logistics</strong> — Transporter, vehicle, LR#, estimated/actual freight, chargeable weight, vendor details</li>
        </ol>
      </div>
    </div>
  );
}
